/**
 * Wo eine Ebene auf der Leinwand liegt — als Zahlen, nicht als Stilblatt.
 *
 * Bisher stand diese Rechnung zweimal da: einmal in CSS für die Vorschau,
 * einmal in ffmpeg für den Export. Die Prüfungen zu `compile.ts` halten die
 * beiden zusammen. Jetzt kommt eine dritte dazu — die Leinwand, die selbst
 * malt (7.69) —, und drei Rechnungen, die dasselbe meinen, laufen ohne eine
 * gemeinsame Stelle garantiert auseinander.
 *
 * Also steht sie hier, einmal, und ist geprüft.
 */
/**
 * Wie ein Bild in die Leinwand passt, ohne verzerrt zu werden.
 *
 * Dasselbe wie `object-fit: contain`: was zu breit ist, bekommt Balken oben
 * und unten, was zu hoch ist, Balken links und rechts.
 */
export function einpassen(quelle, leinwand) {
    if (!(quelle > 0) || !(leinwand > 0))
        return { breite: 1, hoehe: 1 };
    return quelle >= leinwand
        ? { breite: 1, hoehe: leinwand / quelle }
        : { breite: quelle / leinwand, hoehe: 1 };
}
/**
 * Wie eine Ebene über der Basisspur sitzt: so breit wie die Leinwand, und
 * die Höhe folgt dem Bild.
 *
 * Nicht eingepasst wie die Basisspur -- so rechnet der Export
 * (`scale=W*scale:-2`), und so stand es bisher im Stilblatt (`width: 100%`,
 * Höhe `auto`). Ein hochkantes Bild auf einer breiten Leinwand ist damit bei
 * Größe 1 höher als die Leinwand; das ist gewollt, dafür gibt es die Größe.
 */
export function ebenenKasten(quelle, leinwand) {
    if (!(quelle > 0) || !(leinwand > 0))
        return { breite: 1, hoehe: 1 };
    return { breite: 1, hoehe: leinwand / quelle };
}
/**
 * Die vier Ecken einer Ebene, im Koordinatensystem der Grafikkarte.
 *
 * Dort liegt der Nullpunkt in der Mitte, links ist −1 und rechts +1; oben ist
 * +1, weil die Grafikkarte von unten nach oben rechnet und ein Bild von oben
 * nach unten. Genau dieses Umdrehen ist die Stelle, an der ein Bild sonst auf
 * dem Kopf steht.
 *
 * Der Weg ist derselbe wie im Stilblatt und im Export: das eingepasste
 * Rechteck wird um seinen Mittelpunkt gedreht und skaliert und dann dorthin
 * geschoben, wo x/y hinzeigen (0..1, Mitte ist 0,5).
 *
 * Zurück kommen acht Zahlen: links-oben, rechts-oben, links-unten,
 * rechts-unten — die Reihenfolge, in der zwei Dreiecke daraus werden.
 */
export function ebenenEcken(o) {
    const halbBreite = (o.kasten.breite * o.scale) / 2;
    const halbHoehe = (o.kasten.hoehe * o.scale) / 2;
    // **Das Vorzeichen dreht mit.** Ein positiver Winkel dreht im Bild *mit dem
    // Uhrzeigersinn* -- so macht es das Stilblatt (`rotate(30deg)`) und so macht
    // es der Export (am 21. September an ffmpeg nachgemessen: ein Kasten links
    // oben landet bei `rotate=PI/6` dort, wo die Drehung im Uhrzeigersinn ihn
    // hinrechnet). Hier wird in einem System gerechnet, in dem oben +1 ist, und
    // dort ist dieselbe Drehung die entgegengesetzte. Ohne das Minus malte die
    // Grafikkarte jede gedrehte Ebene spiegelbildlich -- und keine Prüfung sah
    // es, weil alle den Winkel symmetrisch abfragten (7.128).
    const bogen = (-o.rotate * Math.PI) / 180;
    const cos = Math.cos(bogen);
    const sin = Math.sin(bogen);
    // Gedreht wird auf einer runden Leinwand: ohne das Verhältnis wird aus
    // einem gedrehten Quadrat auf einem breiten Bild eine Raute.
    const dreh = (dx, dy) => {
        const bx = dx * o.leinwand;
        const x = bx * cos - dy * sin;
        const y = bx * sin + dy * cos;
        return [x / o.leinwand, y];
    };
    const mitteX = (o.x - 0.5) * 2;
    const mitteY = -(o.y - 0.5) * 2;
    const ecken = [];
    for (const [sx, sy] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
    ]) {
        // sy = −1 ist oben im Bild und damit +1 auf der Grafikkarte.
        const [dx, dy] = dreh(sx * halbBreite, -sy * halbHoehe);
        ecken.push(mitteX + dx * 2, mitteY + dy * 2);
    }
    return ecken;
}
/**
 * Der Rahmen, an dem die Vignette gemessen wird (7.69).
 *
 * Der Export misst sie nie am Bild, sondern an dem Rahmen, in den das Bild
 * eingepasst wurde: `scale`+`pad` machen erst die Leinwandform daraus, dann
 * läuft `vignette` darüber. Bei einem eingepassten Clip auf der Basisspur
 * sind das die Balken mit: die Ecken des *Rahmens* werden dunkel, nicht die
 * Ecken des Bildes.
 *
 * Ein Rahmen in Leinwandform hat in Anteilen der Leinwand dieselbe Zahl für
 * Breite und Höhe -- die Anteile sind je Achse gerechnet. Der kleinste, der
 * die Ebene enthält, nimmt also die größere der beiden Zahlen.
 */
export function vignetteRahmen(kasten) {
    const m = Math.max(kasten.breite, kasten.hoehe);
    return { breite: m, hoehe: m };
}
//# sourceMappingURL=leinwand.js.map